import Bugsnag from "@bugsnag/js";
import { ensureBugsnagStarted } from "@/lib/bugsnag";

// Basic patterns to catch common secrets and identifiers
const SENSITIVE_KEYS = [
    /password/i,
    /pwd/i,
    /secret/i,
    /token/i,
    /authorization/i,
    /auth/i,
    /bearer/i,
    /session/i,
    /cookie/i,
];

const SENSITIVE_VALUE_PATTERNS = [
    /bearer\s+[a-z0-9\-_.]+/i,
    /api[_-]?key\s*[:=]\s*[a-z0-9]{8,}/i,
    /secret\s*[:=]\s*\S+/i,
    /password\s*[:=]\s*\S+/i,
    /token\s*[:=]\s*\S+/i,
];

function redactValue(value: unknown): unknown {
    if (value === null || value === undefined) return value;

    if (typeof value === "string") {
        let redacted = value;
        for (const pattern of SENSITIVE_VALUE_PATTERNS) {
            redacted = redacted.replace(pattern, "[REDACTED]");
        }
        return redacted;
    }

    if (Array.isArray(value)) return value.map(redactValue);

    if (typeof value === "object") {
        const out: Record<string, unknown> = {};
        for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
            const shouldRedact = SENSITIVE_KEYS.some((rx) => rx.test(k));
            if (shouldRedact) {
                // Preserve booleans for observability while hiding sensitive strings/objects
                out[k] = typeof v === "boolean" ? v : "[REDACTED]";
                continue;
            }
            out[k] = redactValue(v);
        }
        return out;
    }

    return value;
}

function sanitizeArgs(args: unknown[]): unknown[] {
    return args.map(redactValue);
}

function emit(
    level: "log" | "info" | "warn" | "error" | "debug",
    args: unknown[]
) {
    const safeArgs = sanitizeArgs(args);
    const fn = console[level] || console.log;
    // Forward to Bugsnag breadcrumb for extra tracing without sensitive fields
    try {
        const started = ensureBugsnagStarted();
        if (started) {
            Bugsnag.leaveBreadcrumb("log", {
                level,
                message:
                    typeof safeArgs[0] === "string" ? safeArgs[0] : undefined,
            });
        }
    } catch {}
    fn.apply(console, safeArgs as []);
}

export const logger = {
    info: (...args: unknown[]) => emit("info", args),
    log: (...args: unknown[]) => emit("info", args),
    warn: (...args: unknown[]) => emit("warn", args),
    error: (...args: unknown[]) => emit("error", args),
    debug: (...args: unknown[]) => emit("debug", args),
};

export default logger;
